<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">
        {{ $t('writeOff.pageTitle') }}
      </h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          {
            text: $t('writeOff.pageTitle'),
            active: true
          }
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <div class="row">
          <div class="col-sm-12 col-md-3">
            <ts-button
              color="info"
              outline
              @click="$router.push({ name: 'write-off-create' })"
            >
              {{ $t('add_new') }}
            </ts-button>
          </div>
          <div class="col-sm-12 col-md-4 d-flex justify-content-end"></div>
          <div class="col-sm-12 col-md-5 d-flex justify-content-end">
            <a-range-picker
              v-model="dateRange"
              :allowClear="false"
              format="DD-MM-YYYY"
              valueFormat="DD-MM-YYYY"
              style="width:600px"
              @change="fetchData()"
              class="mr-2"
            />
            <ts-input
              v-model="search"
              :placeholder="$t('search')"
              class="mr-2"
            />
            <ts-button
              outline
              v-tooltip="$t('filter')"
              @click.prevent="visible = true"
            >
              <i class="fa fa-filter"></i>
            </ts-button>
          </div>
        </div>
      </ts-card-wrapper>
      <div class="overflow-auto">
        <ts-table
          :columns="columns"
          :records="resources"
          :loading="loading"
          :rowGroup="{
            field: 'write_off_date'
          }"
        >
          <template v-slot="{ record }">
            <td class="ts-whitespace-no-wrap">
              {{ record.branch.branch_name_en }}
            </td>
            <td class="ts-whitespace-no-wrap ts-text-center">
              {{ record.write_off_number }}
            </td>
            <td class="ts-whitespace-no-wrap ts-text-center">
              {{
                record.ref_stock_in ? record.ref_stock_in.stock_in_number : ''
              }}
            </td>
            <td class="ts-whitespace-no-wrap">
              {{ record.write_off_reason }}
            </td>
            <td class="ts-whitespace-no-wrap ts-text-center">
              <span class="badge badge-success" v-if="record.is_confirm"
                >Yes</span
              >
              <span class="badge badge-danger" v-else>No</span>
            </td>
            <td class="ts-whitespace-no-wrap">
              {{
                record.confirmed_by
                  ? record.confirmed_by.employee
                    ? record.confirmed_by.employee.employee_name_en
                    : ''
                  : ''
              }}
            </td>
            <td class="ts-whitespace-no-wrap ts-text-center">
              {{ record.confirmed_date }}
            </td>
            <td class="ts-whitespace-no-wrap text-center">
              <a
                href="#"
                v-tooltip="$t('view')"
                class="text-info"
                @click.prevent="view(record)"
              >
                <i class="fas fa-eye"></i>
              </a>
              <a
                href="#"
                v-tooltip="$t('edit')"
                class="mr-2 ml-2 text-primary"
                @click.prevent="edit(record)"
              >
                <i class="fas fa-edit"></i>
              </a>
              <a
                href="#"
                v-tooltip="$t('delete')"
                class="text-danger"
                @click.prevent="destroy(record)"
                :disabled="record._deleting"
              >
                <i class="fas fa-circle-notch spin" v-if="record._deleting"></i>
                <i class="fas fa-trash" v-else></i>
              </a>
            </td>
          </template>
        </ts-table>
      </div>
      <div class="d-flex justify-content-end p-15">
        <ts-pagination v-model="pagination" @navigate="fetchData">
        </ts-pagination>
      </div>
    </ts-card>
    <a-drawer
      :title="$t('filter')"
      placement="right"
      :closable="false"
      :visible="visible"
      @close="() => (visible = false)"
      :zIndex="1020"
      width="300px"
    >
      <div class="row">
        <div class="col-md-12">
          <div class="form-group">
            <ts-branch-filter @filter="value => (branch_id = value)" />
          </div>
        </div>
      </div>
    </a-drawer>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import { mapState } from 'vuex'

export default {
  name: 'writeOff',
  data () {
    return {
      loading: false,
      waiting: false,
      visible: false,
      branch_id: []
    }
  },
  computed: {
    ...mapState('stock/writeOff', ['resources', 'pagination']),
    search: {
      get () {
        return this.$store.state.stock.writeOff.search
      },
      set (newValue) {
        this.$store.commit('stock/writeOff/SET_SEARCH', newValue)
        this.$store.commit('stock/writeOff/RESET_CURRENT_PAGE')
      }
    },
    dateRange: {
      get () {
        return this.$store.state.stock.writeOff.dateRange
      },
      set (newValue) {
        this.$store.commit('stock/writeOff/SET_DATE_RANGE', newValue)
      }
    },
    columns () {
      return [
        { name: this.$t('writeOff.branch'), style: 'width:10%' },
        {
          name: this.$t('writeOff.writeOffNumber'),
          style: 'text-align:center; width:8%'
        },
        {
          name: this.$t('writeOff.referenceStockIn'),
          style: 'text-align:center; width:8%'
        },
        { name: this.$t('writeOff.reason') },
        {
          name: this.$t('writeOff.isConfirm'),
          style: 'text-align:center; width:3%'
        },
        { name: this.$t('writeOff.confirmedBy'), style: 'width:10%' },
        {
          name: this.$t('writeOff.confirmedDate'),
          style: 'text-align:center; width:5%'
        },
        {
          name: this.$t('actions'),
          style: 'text-align:center; width:2%'
        }
      ]
    }
  },
  created () {
    this.fetchData()
  },
  methods: {
    fetchData (attributes) {
      this.loading = true
      this.$store
        .dispatch(
          'stock/writeOff/fetch',
          Object.assign(
            {
              branch_id: this.branch_id
            },
            { ...attributes }
          )
        )
        .then(() => {
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.$toasted.error(error.message)
        })
    },
    edit (record) {
      this.$store.commit('stock/writeOff/SET_EDIT_DATA', record)
      this.$router.push({
        name: 'write-off-edit',
        params: { id: record.write_off_id }
      })
    },
    async destroy (record) {
      if (await this.$confirmModal.danger(this.$t('are_you_sure_to_delete'))) {
        record._deleting = true
        this.$store
          .dispatch('stock/writeOff/destroy', record.write_off_id)
          .then(response => {
            this.fetchData()
            this.$toasted.success(response.message)
            record._deleting = false
          })
          .catch(error => {
            if (
              error.message ===
              'The current user not yet belong to any warehouses.'
            ) {
              this.$toasted.info(error.message)
            } else {
              this.$toasted.error(error.message)
            }
            record._deleting = false
          })
      }
    },
    view (data) {
      this.$store.commit('stock/writeOff/SET_EDIT_DATA', data)
      this.$router.push({
        name: 'write-off-show',
        params: { id: data.write_off_id }
      })
    },
    format_number (value) {
      return parseFloat(value).toFixed(2)
    }
  },
  watch: {
    search: debounce(function (search) {
      this.fetchData({ search })
    }, 500),
    branch_id: debounce(function (value) {
      this.fetchData({ branch_id: value })
    }, 500)
  },
  beforeRouteLeave (to, from, next) {
    if (_.isEmpty(to.params) && to.name != 'write-off-create') {
      this.$store.commit('stock/writeOff/RESET_STATE')
    }
    next()
  }
}
</script>
